import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { GenericPageError } from '@components/core-ui/GenericPageError';
import { Wrapper } from '@modals/Admin/AdminBackupFilesModal/styles/Wrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { JobsAdminService } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { useEffect } from 'react';
import { createFetchBackupMixdownStatusQueryFn } from './api/createFetchBackupMixdownStatusQueryFn';
import { createFetchJobBackupFilesQueryFn } from './api/createFetchJobBackupFilesQueryFn';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  id: string;
};

const MIXDOWN_STATUS_POLL_INTERVAL_MS = 10000;

export const AdminBackupFilesModal = ({ onCancel, onSuccess, id }: TProps) => {
  const { call } = useAPI();
  const { closeModal, openModal } = useModalContext();

  const filesQuery = useQuery({
    queryKey: adminJobsKeys.backupFilesByJob(id),
    queryFn: createFetchJobBackupFilesQueryFn(call),
  });
  useNotifyQueryError(filesQuery.error);

  const mixdownMutation = useMutation({
    mutationFn: () => call(() => JobsAdminService.adminMixdownJobBackupFiles({ jobId: id })),
    onError: notifyApiCallError,
  });

  const mixdownJobId = mixdownMutation.data;

  const mixdownStatusQuery = useQuery({
    queryKey: adminJobsKeys.backupMixdownStatus(mixdownJobId ?? ''),
    queryFn: createFetchBackupMixdownStatusQueryFn(call),
    enabled: !!mixdownJobId,
    refetchInterval: (query) =>
      query.state.status === 'error' || query.state.data?.returnvalue || query.state.data?.failedReason
        ? false
        : MIXDOWN_STATUS_POLL_INTERVAL_MS,
  });

  useNotifyQueryError(mixdownStatusQuery.error);

  const mixdownStatus = mixdownStatusQuery.data;

  useEffect(() => {
    if (mixdownStatus?.returnvalue) {
      closeModal();
      onSuccess();
      return;
    }
    if (mixdownStatus?.failedReason) {
      openModal(ModalNamesEnum.GenericErrorModal, {
        message: 'Backup Files Processing Error',
      });
    }
  }, [mixdownStatus]);

  const files = filesQuery.data ?? [];
  const isLoading =
    filesQuery.isLoading ||
    mixdownMutation.isPending ||
    (!!mixdownJobId && !mixdownStatus?.failedReason && !mixdownStatusQuery.isError);
  const onSubmit = () => mixdownMutation.mutate();

  if (!isLoading && !files.length) {
    return (
      <Wrapper>
        <ModalWrapper width="400px">
          <ModalTitle>Attach Backup Files</ModalTitle>

          <GenericPageError
            message={filesQuery.isError ? 'Failed to load the backup files. Please try again.' : 'No files to attach'}
          />

          <div>
            <Button onClick={onCancel}>Ok</Button>
          </div>
        </ModalWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ModalWrapper width="400px">
        {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
        <ModalTitle>Attach Backup Files</ModalTitle>

        <div className="files">
          {files.map((file, index) => (
            <div key={`${file}-${index}`} className="file">
              {file}
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isLoading} onClick={onSubmit}>
            Attach
          </Button>
        </div>
      </ModalWrapper>
    </Wrapper>
  );
};
